import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useOnboarding } from "../../hooks/useOnboarding";
import styles from "./ConnectOAuth.module.css";
import onboardingService from "../../services/onboardingService";
import InlineSpinner from "../../components/common/InlineSpinner";
import { ContactsService } from "../../services/contactsService";

interface SelectContactsProps {
  onNext: (data?: any) => void;
  onBack?: () => void;
}

interface ContactEntry {
  email: string;
  name: string;
  isVCContact?: boolean;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const SelectContacts: React.FC<SelectContactsProps> = ({ onNext, onBack }) => {
  const { user } = useAuth();
  const { data } = useOnboarding();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [entries, setEntries] = useState<ContactEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVCInvite, setIsVCInvite] = useState(false);

  const isValidEmail = (value: string) => emailRegex.test(value.trim());

  // Auto-populate VC email for portfolio companies invited by VCs
  useEffect(() => {
    try {
      const inviteDataStr = localStorage.getItem('portfolio_invite_data');
      if (inviteDataStr) {
        const inviteData = JSON.parse(inviteDataStr);
        if (inviteData.isVCInvite) {
          setIsVCInvite(true);
          const vcEmail = inviteData.vcEmail;
          const vcName = inviteData.vcName || 'Your VC';

          if (vcEmail) {
            setEntries(prev => {
              // Only add if not already in list (case-insensitive check)
              const exists = prev.some(
                e => e.email.toLowerCase() === vcEmail.toLowerCase()
              );
              if (!exists) {
                console.log('SelectContacts: Auto-populating VC contact', { vcEmail, vcName });
                return [{
                  email: vcEmail.toLowerCase(),
                  name: vcName,
                  isVCContact: true,
                }, ...prev];
              }
              return prev;
            });
          }
        }
      }
    } catch (e) {
      console.log('SelectContacts: Failed to read VC invite data', e);
    }
  }, []);

  // Extracts an email address from user input that may include a display name,
  // angle brackets, trailing punctuation, or a mailto: prefix
  const extractEmailFromInput = (raw: string): string | null => {
    let input = (raw || "").trim();
    if (!input) return null;

    // Prefer an address inside angle brackets if present
    const angleMatch = input.match(/<([^<>\s]+@[^<>\s]+)>/);
    if (angleMatch && angleMatch[1]) {
      input = angleMatch[1];
    }

    // Remove common prefixes/suffixes and quotes
    input = input.replace(/^mailto:/i, "");
    input = input.replace(/^"|"$/g, "");
    input = input.replace(/[;,]+$/g, "");

    // Find the first email-like token
    const tokenMatch = input.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    const candidate = tokenMatch ? tokenMatch[0] : input;

    const normalized = candidate.toLowerCase().trim();
    return isValidEmail(normalized) ? normalized : null;
  };

  // Hydrate from DB on mount so state persists when user navigates back
  useEffect(() => {
    let mounted = true;
    const hydrateFromDb = async () => {
      try {
        console.log("SelectContacts:hydrateFromDb:start", { userId: user?.id });
        if (!user?.id) return;
        const contacts = await ContactsService.getUserContacts();
        if (!mounted) return;
        const mapped = (contacts || [])
          .filter((c: any) => c && c.email)
          .map((c: any) => ({ email: c.email, name: c.name || "" }));
        // Avoid overwriting VC contact or user-typed entries
        setEntries((prev) => {
          // If we have a VC contact, preserve it and add DB contacts
          const vcContact = prev.find(e => e.isVCContact);
          if (vcContact) {
            // Filter out duplicates from DB that match VC email
            const filteredMapped = mapped.filter(
              (m: ContactEntry) => m.email.toLowerCase() !== vcContact.email.toLowerCase()
            );
            return [vcContact, ...filteredMapped];
          }
          // Otherwise, only hydrate if empty
          return prev.length > 0 ? prev : mapped;
        });
        console.log("SelectContacts:hydrateFromDb:done", {
          mappedCount: mapped.length,
        });
      } catch (e) {
        // Non-blocking: silently ignore hydration errors
        console.log("SelectContacts: hydration failed", e);
      }
    };
    hydrateFromDb();
    return () => {
      mounted = false;
    };
  }, [user?.id]);

  // Debug state changes
  useEffect(() => {
    console.log("SelectContacts:state", {
      entriesCount: entries.length,
      isSubmitting,
      hasError: !!error,
    });
  }, [entries.length, isSubmitting, error]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleAdd = () => {
    setError(null);
    const extractedPrimary = extractEmailFromInput(email);
    const extractedFromName = extractEmailFromInput(name);
    const extracted = extractedPrimary || extractedFromName;
    console.log("SelectContacts:handleAdd", {
      rawEmail: email,
      rawName: name,
      extracted,
      source: extractedPrimary ? "email" : extractedFromName ? "name" : null,
    });
    if (!extracted) {
      setError("Please enter a valid email address.");
      console.log("SelectContacts:handleAdd:invalid", { raw: email });
      return;
    }
    // Preserve a human-entered name when email was pasted into the name field:
    // - If email was taken from the Email field, use Name field as display name
    // - If email was taken from the Name field, try using the Email field as display name
    //   only when it doesn't itself look like an email (e.g., "Chris").
    const possibleNameFromEmailField = email.trim();
    const finalName = extractedPrimary
      ? name.trim()
      : isValidEmail(possibleNameFromEmailField)
      ? ""
      : possibleNameFromEmailField;

    const newEntry: ContactEntry = {
      email: extracted,
      name: finalName,
    };
    setEntries((prev) => [newEntry, ...prev]);
    setEmail("");
    setName("");
  };

  const handleRemove = (idx: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleContinue = async () => {
    if (!user) {
      setError("User not found. Please log in again.");
      return;
    }
    if (entries.length === 0) {
      setError("Add at least one contact to continue.");
      return;
    }
    console.log("SelectContacts:handleContinue:start", {
      userId: user?.id,
      entriesCount: entries.length,
    });
    setIsSubmitting(true);
    setError(null);
    try {
      for (const entry of entries) {
        try {
          console.log("SelectContacts:createContact:request", entry);
          await ContactsService.createContact({
            email: entry.email,
            name: entry.name || undefined,
          } as any);
          console.log("SelectContacts:createContact:success", entry.email);
        } catch (e) {
          console.error("Failed to create contact:", entry.email, e);
        }
      }

      const currentOnboardingData = {
        ...data,
        contactsAdded: entries.length,
        step: 4,
        completed_steps: [
          ...(data.completed_steps || []),
          "contacts_selection",
        ],
      } as any;
      console.log("SelectContacts:updateOnboarding:request", {
        userId: user.id,
        contactsAdded: entries.length,
      });
      await onboardingService.updateOnboarding(user.id, currentOnboardingData);
      console.log("SelectContacts:updateOnboarding:success");

      onNext({ contactsAdded: entries.length });
    } catch (err) {
      console.error("Error saving contacts:", err);
      setError("Failed to save contacts. Please try again.");
    } finally {
      console.log("SelectContacts:handleContinue:finally");
      setIsSubmitting(false);
    }
  };

  const canContinue = entries.length > 0 && !isSubmitting;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Add Your First Contacts</h1>
        <p className={styles.subtitle}>
          {isVCInvite
            ? "We've added your VC as the first contact. You can add more contacts or continue."
            : "Tip: you can add yourself as the first contact."}
        </p>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.providerCard}>
        <div className={styles.providerHeader}>
          <h3>Add a contact</h3>
        </div>
        <div className={styles.inputRow}>
          <input
            type="text"
            placeholder="Name (optional)"
            value={name}
            onChange={(e) => {
              console.log("SelectContacts:name:onChange", e.target.value);
              setName(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            autoComplete="name"
            className={styles.input}
          />
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => {
              console.log("SelectContacts:email:onChange", e.target.value);
              setEmail(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            autoComplete="email"
            className={styles.input}
          />
          <button
            type="button"
            onClick={handleAdd}
            className={styles.connectButton}
          >
            Add
          </button>
        </div>

        {entries.length > 0 && (
          <div className={styles.list}>
            {entries.map((entry, idx) => (
              <div key={`${entry.email}-${idx}`} className={styles.itemRow}>
                <div className={styles.itemText}>
                  <div className={styles.itemEmail}>
                    {entry.email}
                    {entry.isVCContact && (
                      <span
                        style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          backgroundColor: '#7c3aed',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                        }}
                      >
                        VC
                      </span>
                    )}
                  </div>
                  {entry.name && (
                    <div className={styles.itemName}>{entry.name}</div>
                  )}
                </div>
                {!entry.isVCContact && (
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => handleRemove(idx)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.buttonGroup}>
        {onBack && (
          <button className={styles.skipButton} onClick={onBack}>
            Back
          </button>
        )}
        <div className={styles.rightButtons}>
          <button
            onClick={handleContinue}
            className={styles.connectButton}
            disabled={!canContinue}
          >
            {isSubmitting ? <InlineSpinner /> : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectContacts;
